import { useEffect, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { debugState } from '../lib/debugState'
import { faceEvalResult } from '../lib/faceEvalResult'
import { faceClipPlayer } from '../lib/FaceClipPlayer'
import { applyBlink } from '../lib/blinkRuntime'
import { applyEyeFollow } from '../lib/eyeFollowRuntime.js'
import { tracePhase } from '../lib/perfProbe'
import { isArkitForced, getCurrentArkit } from '../network/AudioAnimationBuffer'
import { isFaceAnimEnabled } from '../lib/faceAnimState'
import { composeRigControlArkit, rigControlWeight } from '../lib/rigControlState'
import { faceTrackState, faceTrackWeight } from '../lib/faceTrackState.js'
import { currentBlinkWeight } from '../lib/blinkRuntime'
import { eyeFollowRuntime } from '../lib/eyeFollowRuntime.js'
import { faceRigDrive } from '../lib/faceRigDriver.js'
import { selectFaceArkitFrame } from '../lib/faceFrame.js'

// Scratch for the joint-driven face (faceRigDriver.js): the resolved ARKit frame
// and the source record faceFrame.js reads. Module-level, so a frame allocates
// nothing; only the fields are rewritten.
const _faceFrame = new Float32Array(52)
const _faceFrameSrc = { forcedArkit: null, rigWeight: 0, rigArkit: null, trackWeight: 0, trackArkit: null, liveWeight: 0, liveArkit: null, clipWeights: null, blink: 0, eyeOverlay: null }

// After aosTools/threejs/combine_avatar.py joins the body + head into a single mesh
// and Blender's GLTF exporter splits it back into one primitive per
// material, we end up with multiple THREE.Mesh objects in the scene that
// each carry the full set of 52 ARKit morph targets (glTF morph targets
// are per-primitive). Only the head-material primitive has meaningful
// delta data for the face; the body-material primitive has zeros. But
// we don't know which is which, so we drive morphs on ALL meshes that
// have morph-target arrays.

export default function HeadModel({ animationBuffer, onLoaded }) {
  const { scene: threeScene, gl, camera } = useThree()
  const meshesRef = useRef([])
  const notifiedRef = useRef(false)
  const faceClipsLoadedRef = useRef(false)
  const shadersWarmedRef = useRef(false)
  // Tracks whether we've zeroed all morph influences since the last time
  // anything was driving the face. Skips ~4×52 fill-with-0 writes per frame
  // during the idle-face state (most of the session).
  const morphsZeroedRef = useRef(false)

  // Defer face-clip loading until after the avatar + rig are ready so the
  // initial page paint isn't competing with 12 JSON fetches. Triggered via
  // requestIdleCallback once __AVATAR_READY__ flips true.
  useEffect(() => {
    if (faceClipsLoadedRef.current) return
    let cancelled = false
    const tryStart = () => {
      if (cancelled || faceClipsLoadedRef.current) return
      if (!window.__AVATAR_READY__) {
        // Still waiting on avatar — re-check on the next idle tick
        schedule(tryStart)
        return
      }
      faceClipsLoadedRef.current = true
      faceClipPlayer.loadAll()
    }
    const schedule = (cb) => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(cb, { timeout: 2000 })
      } else {
        setTimeout(cb, 250)
      }
    }
    schedule(tryStart)
    return () => { cancelled = true }
  }, [])

  useFrame(() => {
    // Collect every morph-target mesh once AvatarModel has added them
    if (meshesRef.current.length === 0) {
      const found = []
      threeScene.traverse((n) => {
        if (n.isMesh && n.morphTargetInfluences && n.morphTargetInfluences.length > 0) {
          found.push(n)
        }
      })
      if (found.length > 0) {
        meshesRef.current = found
        if (animationBuffer) {
          // Build the mapping from the first mesh's dictionary (all
          // primitives of a joined mesh share the same shape-key names)
          animationBuffer.buildMapping(found[0].morphTargetDictionary)
        }
        // Pre-warm every shader variant the scene will need — notably the
        // morph-target variant. Without this, the first frame that drives
        // morph targets compiles the shader on the render thread and shows
        // up as a ~100ms untattributed long-task during talking.
        if (!shadersWarmedRef.current) {
          shadersWarmedRef.current = true
          try {
            // Nudge the first morph target so the compile includes the
            // morph variant. Revert immediately after compile.
            for (const m of found) {
              if (m.morphTargetInfluences?.length) m.morphTargetInfluences[0] = 0.0001
            }
            gl.compile(threeScene, camera)
            for (const m of found) {
              if (m.morphTargetInfluences?.length) m.morphTargetInfluences[0] = 0
            }
          } catch (e) {
            console.warn('[HeadModel] shader pre-warm failed:', e)
          }
        }
        if (!notifiedRef.current) {
          notifiedRef.current = true
          // __AVATAR_READY__ is flipped by AvatarModel once the rig is
          // mounted — HeadModel only enriches avatarInfo with the morph
          // count (and then only if the rig actually has blendshapes).
          onLoaded?.({ faceMeshLoaded: true, morphTargetCount: found[0].morphTargetInfluences.length })
          console.log(`[HeadModel] found ${found.length} morph-target meshes: ` +
            found.map(m => `"${m.name}"(${m.morphTargetInfluences.length})`).join(', '))
        }
      }
    }

    // ── Face blueprint evaluation is owned by AvatarModel.useFrame. Read its output. ──
    const faceWeights = faceEvalResult.weights
    const liveWeight  = faceEvalResult.liveWeight

    // Get face clip ARKit weights (from pre-converted JSONs)
    const clipWeights = faceClipPlayer.getBlendedWeights(faceWeights ?? {}, faceEvalResult.plays)

    // Expose face-clip timings so RuleEvaluator (AnimTimeRemaining) can read
    // them. writeTimingsInto mutates debugState.clipTimings in place — saves
    // both the intermediate object alloc and an Object.entries iteration.
    faceClipPlayer.writeTimingsInto(debugState.clipTimings)

    // A joint-driven face (faceRigDriver.js) runs this block too: a MetaHuman
    // GLB has no morph meshes, but the same source resolution decides its frame,
    // and the live path's applyToMeshes advances the buffer with or without meshes.
    if ((meshesRef.current.length > 0 || faceRigDrive.active) && !window.__SKIP_MORPH_ZEROING__) {
      tracePhase('applyMorphs', () => {
        // Debug-forced ARKit pose acts as if LiveLink were active at full
        // weight, so the avatar visibly reflects whatever
        // window.__FORCE_ARKIT__(...) sets even when no Convorcher
        // response is playing.
        //
        // A vertex-driving face source that needs the buffer to advance must
        // declare its own sentinelClip and be wired in here deliberately; the
        // OGS branch does not, because it pulls ARKit itself.
        // Face-anim toggle (Anim Test Panel): OFF takes the idle zero-once branch below.
        const faceAnimOff = !isFaceAnimEnabled()
        const forced = faceAnimOff ? false : isArkitForced()
        // Rig Controller (ENG-946): the Rig Preview sliders drive the face.
        // __FORCE_ARKIT__ is the older, blunter debug seam and stays on top —
        // otherwise a forced pose would look broken while a rig node is wired.
        const rigWeight = (forced || faceAnimOff) ? 0 : rigControlWeight(faceWeights)
        // Webcam Face Tracker node (ENG-1107): outranked by the rig controller and
        // the forced pose, outranks the server's LiveLink stream — the same
        // order faceFrame.js states once for every renderer.
        const trackWeight = (forced || faceAnimOff || rigWeight > 0) ? 0 : faceTrackWeight(faceWeights)
        const effectiveLiveWeight = faceAnimOff ? 0 : (forced ? 1.0 : liveWeight)
        if (rigWeight > 0 && animationBuffer?._mapping) {
          // Advance the buffer ourselves: applyToMeshes isn't running on this
          // path, and without it getCurrentArkit() would go stale — so
          // passthrough mode would freeze on whatever frame was last played.
          // (Same trap the removed sentinel documents above.)
          animationBuffer.advancePlayback()
          const frame = composeRigControlArkit(getCurrentArkit())
          for (const mesh of meshesRef.current) {
            const influences = mesh.morphTargetInfluences
            if (!influences) continue
            for (let i = 0; i < 52; i++) {
              const glbIdx = animationBuffer._mapping[i]
              if (glbIdx !== -1) influences[glbIdx] = Math.min(frame[i] * rigWeight, 1.0)
            }
          }
          morphsZeroedRef.current = false
        } else if (trackWeight > 0 && animationBuffer?._mapping) {
          // Keep the buffer's clock moving so a hand-back to LiveLink is not stale.
          animationBuffer.advancePlayback()
          const frame = faceTrackState.arkit
          for (const mesh of meshesRef.current) {
            const influences = mesh.morphTargetInfluences
            if (!influences) continue
            for (let i = 0; i < 52; i++) {
              const glbIdx = animationBuffer._mapping[i]
              if (glbIdx !== -1) influences[glbIdx] = Math.min(frame[i] * trackWeight, 1.0)
            }
          }
          morphsZeroedRef.current = false
        } else if (effectiveLiveWeight > 0 && animationBuffer) {
          // LiveLink (and/or a vertex-driving face source) drives morphs from
          // server audio animation (or from the forced array — handled inside
          // applyToMeshes). The hidden head_lod0_mesh still gets morph updates
          // here; visibility is what hides it, not the morph values, so this is
          // harmless.
          animationBuffer.applyToMeshes(meshesRef.current, effectiveLiveWeight)
          morphsZeroedRef.current = false
        } else if (clipWeights && !faceAnimOff) {
          // Face clips drive morphs when LiveLink is off
          for (const mesh of meshesRef.current) {
            const influences = mesh.morphTargetInfluences
            if (!influences) continue
            // Use the same mapping as AudioAnimationBuffer if available
            if (animationBuffer?._mapping) {
              for (let i = 0; i < 52 && i < clipWeights.length; i++) {
                const glbIdx = animationBuffer._mapping[i]
                if (glbIdx !== -1) influences[glbIdx] = Math.min(clipWeights[i], 1.0)
              }
            } else {
              const limit = Math.min(52, influences.length)
              for (let i = 0; i < limit; i++) influences[i] = Math.min(clipWeights[i], 1.0)
            }
          }
          morphsZeroedRef.current = false
        } else if (!morphsZeroedRef.current) {
          // Transition from "driven" to "idle" — zero once and remember.
          // Most of the session is idle-face; zeroing every frame was ~4×52
          // typed-array writes / frame for no visual change.
          for (const m of meshesRef.current) {
            if (m.morphTargetInfluences) m.morphTargetInfluences.fill(0)
          }
          morphsZeroedRef.current = true
        }

        // Procedural blink overlay — runs after clip/livelink application so
        // the blink adds on top of whatever the face is doing. Driven by the
        // `blink` graph node which emits __blink__ sentinel weight.
        const blinkWeight = faceAnimOff ? 0 : (faceWeights?.['__blink__'] ?? 0)
        if (blinkWeight > 0) {
          applyBlink(meshesRef.current, animationBuffer?._mapping ?? null, blinkWeight)
          morphsZeroedRef.current = false
        }

        // Procedural eye-look overlay (ENG-1060) — same placement and reasoning
        // as the blink above: after clip/livelink application, so gaze adds on
        // top of the face instead of fighting it for the channel. The aim itself
        // is advanced once per frame by whichever renderer owns the avatar
        // (AvatarModel here); this only spends it.
        const eyeFollowWeight = faceWeights?.['__eyefollow__'] ?? 0
        if (eyeFollowWeight > 0) {
          applyEyeFollow(meshesRef.current, animationBuffer?._mapping ?? null, eyeFollowWeight)
          morphsZeroedRef.current = false
        }

        // Joint-driven face: the SAME owner and overlays the morph writes above
        // followed, resolved once as a value (faceFrame.js) and handed to
        // RigLogic. After the mixer (AvatarModel.useFrame ran first), so the
        // body joints the DNA also carries keep the clip's pose.
        if (faceRigDrive.active) {
          const current = getCurrentArkit()    // the forced array while forced, else the buffer's frame
          _faceFrameSrc.forcedArkit = forced ? current : null
          _faceFrameSrc.rigWeight = rigWeight
          _faceFrameSrc.rigArkit = rigWeight > 0 ? composeRigControlArkit(current) : null
          _faceFrameSrc.trackWeight = trackWeight
          _faceFrameSrc.trackArkit = trackWeight > 0 ? faceTrackState.arkit : null
          _faceFrameSrc.liveWeight = effectiveLiveWeight
          _faceFrameSrc.liveArkit = current
          _faceFrameSrc.clipWeights = clipWeights
          // With morph meshes present, applyBlink already stepped the blink this
          // frame and the lids blink through the morphs; stepping it twice would
          // shorten every blink, so the joints take the blink only on a rig with
          // no morphs at all — which is the rig this driver exists for.
          _faceFrameSrc.blink = (blinkWeight > 0 && meshesRef.current.length === 0) ? currentBlinkWeight(blinkWeight) : 0
          _faceFrameSrc.eyeOverlay = (eyeFollowWeight > 0 && eyeFollowRuntime.state.weight > 0.001) ? eyeFollowRuntime.arkitOverlay() : null
          faceRigDrive.owner = selectFaceArkitFrame(_faceFrameSrc, _faceFrame)
          faceRigDrive.drive(_faceFrame)
        }

      })
    }
  })

  return null
}
